import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useAuth } from '@/hooks/useAuth';
import { RetailWidgetKey } from '@/hooks/useDashboardPreferences';
import { TimePeriod } from '@/hooks/useAnalytics';

// ---- Date range helpers (same logic as useAnalytics) ----

function getStartOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getEndOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function getStartOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getStartOfMonth(date: Date): Date {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

interface DateRange {
  start: Date;
  end: Date;
  comparisonStart: Date;
  comparisonEnd: Date;
  comparisonLabel: string;
}

function getDateRange(timePeriod: TimePeriod): DateRange {
  const now = new Date();
  switch (timePeriod) {
    case 'today': {
      const start = getStartOfDay(now);
      const end = getEndOfDay(now);
      const comparisonStart = new Date(start);
      comparisonStart.setDate(comparisonStart.getDate() - 7);
      const comparisonEnd = new Date(end);
      comparisonEnd.setDate(comparisonEnd.getDate() - 7);
      return { start, end, comparisonStart, comparisonEnd, comparisonLabel: 'last week' };
    }
    case 'week': {
      const start = getStartOfWeek(now);
      const end = getEndOfDay(now);
      const comparisonStart = new Date(start);
      comparisonStart.setDate(comparisonStart.getDate() - 7);
      const comparisonEnd = new Date(end);
      comparisonEnd.setDate(comparisonEnd.getDate() - 7);
      return { start, end, comparisonStart, comparisonEnd, comparisonLabel: 'last week' };
    }
    case 'month': {
      const start = getStartOfMonth(now);
      const end = getEndOfDay(now);
      const comparisonStart = new Date(start);
      comparisonStart.setMonth(comparisonStart.getMonth() - 1);
      const comparisonEnd = new Date(end);
      comparisonEnd.setMonth(comparisonEnd.getMonth() - 1);
      return { start, end, comparisonStart, comparisonEnd, comparisonLabel: 'last month' };
    }
  }
}

function pctChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

// ---- Result types ----

export interface SimpleMetric {
  value: number;
  previousValue: number;
  change: number;
  comparisonLabel: string;
}

export interface PercentageMetric {
  percentage: number;
  previousPercentage: number;
  count: number;
  total: number;
  comparisonLabel: string;
}

export interface TopProductItem {
  productId: string | null;
  name: string;
  sku: string | null;
  quantity: number;
  revenue: number;
  imageUrl: string | null;
}

export interface TopSalespersonItem {
  userId: string;
  name: string;
  avatarUrl: string | null;
  revenue: number;
  orderCount: number;
}

export interface GrossProfitChartPoint {
  label: string;
  grossProfit: number;
}

export interface RevenueChartPoint {
  label: string;
  revenue: number;
}

export interface RetailMetricsData {
  revenue: SimpleMetric | null;
  grossProfit: (SimpleMetric & { chartData: GrossProfitChartPoint[] }) | null;
  saleCount: SimpleMetric | null;
  salesWithCustomerDetails: PercentageMetric | null;
  avgItemsPerSale: SimpleMetric | null;
  avgSaleValue: SimpleMetric | null;
  discounted: (SimpleMetric & { discountedOrderCount: number; totalOrderCount: number }) | null;
  mySalesPerformance: (SimpleMetric & { orderCount: number; previousOrderCount: number }) | null;
  topProductsSold: TopProductItem[] | null;
  topSalesPeople: TopSalespersonItem[] | null;
  comparisonLabel: string;
  isLoading: boolean;
}

interface UseRetailMetricsOptions {
  timePeriod: TimePeriod;
  globalMode: boolean;
  enabledWidgets: RetailWidgetKey[];
}

export function useRetailMetrics(options: UseRetailMetricsOptions): RetailMetricsData {
  const { timePeriod, globalMode, enabledWidgets } = options;
  const { currentLocation, locations } = useLocationStore();
  const { user } = useAuth();

  const effectiveLocationIds = globalMode
    ? locations.map(l => l.id)
    : currentLocation?.id
      ? [currentLocation.id]
      : [];

  const dateRange = getDateRange(timePeriod);

  // Determine which query groups we need based on enabled widgets
  const needsBasicOrders = enabledWidgets.some(w =>
    ['revenue', 'sale_count', 'avg_items_per_sale', 'avg_sale_value',
     'sales_with_customer_details', 'discounted'].includes(w)
  );
  const needsGrossProfit = enabledWidgets.includes('gross_profit');
  const needsMySales = enabledWidgets.includes('my_sales_performance');
  const needsTopProducts = enabledWidgets.includes('top_products_sold');
  const needsTopSalesPeople = enabledWidgets.includes('top_sales_people');

  // ---------- Basic order metrics query ----------
  const basicQuery = useQuery({
    queryKey: ['retail-metrics-basic', effectiveLocationIds.sort().join(','), timePeriod],
    queryFn: async () => {
      if (effectiveLocationIds.length === 0) return null;

      // Current period
      let currentQ = supabase
        .from('orders')
        .select('id, total, discount_amount, customer_id, user_id, created_at, order_items(id, quantity)')
        .gte('created_at', dateRange.start.toISOString())
        .lte('created_at', dateRange.end.toISOString())
        .in('status', ['completed', 'ready', 'in_progress', 'sent']);

      if (effectiveLocationIds.length === 1) {
        currentQ = currentQ.eq('location_id', effectiveLocationIds[0]);
      } else {
        currentQ = currentQ.in('location_id', effectiveLocationIds);
      }

      const { data: currentOrders, error: err1 } = await currentQ;
      if (err1) throw err1;

      // Comparison period
      let compQ = supabase
        .from('orders')
        .select('id, total, discount_amount, customer_id, user_id, created_at, order_items(id, quantity)')
        .gte('created_at', dateRange.comparisonStart.toISOString())
        .lte('created_at', dateRange.comparisonEnd.toISOString())
        .in('status', ['completed', 'ready', 'in_progress', 'sent']);

      if (effectiveLocationIds.length === 1) {
        compQ = compQ.eq('location_id', effectiveLocationIds[0]);
      } else {
        compQ = compQ.in('location_id', effectiveLocationIds);
      }

      const { data: compOrders, error: err2 } = await compQ;
      if (err2) throw err2;

      return { currentOrders: currentOrders || [], compOrders: compOrders || [] };
    },
    enabled: effectiveLocationIds.length > 0 && needsBasicOrders,
    staleTime: 60000,
  });

  // ---------- Gross profit query (needs product costs) ----------
  const grossProfitQuery = useQuery({
    queryKey: ['retail-metrics-gross-profit', effectiveLocationIds.sort().join(','), timePeriod],
    queryFn: async () => {
      if (effectiveLocationIds.length === 0) return null;

      // We need to filter by location via the parent order
      // Use a subquery approach: get order IDs first, then items
      let orderIdsQ = supabase
        .from('orders')
        .select('id, created_at')
        .gte('created_at', dateRange.start.toISOString())
        .lte('created_at', dateRange.end.toISOString())
        .in('status', ['completed', 'ready', 'in_progress', 'sent']);

      if (effectiveLocationIds.length === 1) {
        orderIdsQ = orderIdsQ.eq('location_id', effectiveLocationIds[0]);
      } else {
        orderIdsQ = orderIdsQ.in('location_id', effectiveLocationIds);
      }

      const { data: currentOrderIds } = await orderIdsQ;
      const ids = (currentOrderIds || []).map(o => o.id);

      if (ids.length === 0) {
        return { currentItems: [], compItems: [], currentOrderDates: [], compOrderDates: [] };
      }

      // Fetch current items in batches if needed
      const { data: currentItems, error: e1 } = await supabase
        .from('order_items')
        .select('id, quantity, unit_price, total, order_id, created_at, products(id, cost)')
        .in('order_id', ids);
      if (e1) throw e1;

      // Comparison period
      let compOrderIdsQ = supabase
        .from('orders')
        .select('id, created_at')
        .gte('created_at', dateRange.comparisonStart.toISOString())
        .lte('created_at', dateRange.comparisonEnd.toISOString())
        .in('status', ['completed', 'ready', 'in_progress', 'sent']);

      if (effectiveLocationIds.length === 1) {
        compOrderIdsQ = compOrderIdsQ.eq('location_id', effectiveLocationIds[0]);
      } else {
        compOrderIdsQ = compOrderIdsQ.in('location_id', effectiveLocationIds);
      }

      const { data: compOrderIds } = await compOrderIdsQ;
      const compIds = (compOrderIds || []).map(o => o.id);

      let compItems: any[] = [];
      if (compIds.length > 0) {
        const { data, error: e2 } = await supabase
          .from('order_items')
          .select('id, quantity, unit_price, total, order_id, created_at, products(id, cost)')
          .in('order_id', compIds);
        if (e2) throw e2;
        compItems = data || [];
      }

      // Build date mapping for chart data
      const currentOrderDates = (currentOrderIds || []).map(o => ({
        id: o.id,
        created_at: o.created_at,
      }));
      const compOrderDates = (compOrderIds || []).map(o => ({
        id: o.id,
        created_at: o.created_at,
      }));

      return {
        currentItems: currentItems || [],
        compItems,
        currentOrderDates,
        compOrderDates,
      };
    },
    enabled: effectiveLocationIds.length > 0 && needsGrossProfit,
    staleTime: 60000,
  });

  // ---------- My sales performance query ----------
  const mySalesQuery = useQuery({
    queryKey: ['retail-metrics-my-sales', effectiveLocationIds.sort().join(','), timePeriod, user?.id],
    queryFn: async () => {
      if (effectiveLocationIds.length === 0 || !user?.id) return null;

      let currentQ = supabase
        .from('orders')
        .select('id, total')
        .eq('user_id', user.id)
        .gte('created_at', dateRange.start.toISOString())
        .lte('created_at', dateRange.end.toISOString())
        .in('status', ['completed', 'ready', 'in_progress', 'sent']);

      if (effectiveLocationIds.length === 1) {
        currentQ = currentQ.eq('location_id', effectiveLocationIds[0]);
      } else {
        currentQ = currentQ.in('location_id', effectiveLocationIds);
      }

      const { data: currentOrders, error: e1 } = await currentQ;
      if (e1) throw e1;

      let compQ = supabase
        .from('orders')
        .select('id, total')
        .eq('user_id', user.id)
        .gte('created_at', dateRange.comparisonStart.toISOString())
        .lte('created_at', dateRange.comparisonEnd.toISOString())
        .in('status', ['completed', 'ready', 'in_progress', 'sent']);

      if (effectiveLocationIds.length === 1) {
        compQ = compQ.eq('location_id', effectiveLocationIds[0]);
      } else {
        compQ = compQ.in('location_id', effectiveLocationIds);
      }

      const { data: compOrders, error: e2 } = await compQ;
      if (e2) throw e2;

      return { currentOrders: currentOrders || [], compOrders: compOrders || [] };
    },
    enabled: effectiveLocationIds.length > 0 && needsMySales && !!user?.id,
    staleTime: 60000,
  });

  // ---------- Top products sold query ----------
  const topProductsQuery = useQuery({
    queryKey: ['retail-metrics-top-products', effectiveLocationIds.sort().join(','), timePeriod],
    queryFn: async () => {
      if (effectiveLocationIds.length === 0) return null;

      // Get order IDs for the period
      let orderIdsQ = supabase
        .from('orders')
        .select('id')
        .gte('created_at', dateRange.start.toISOString())
        .lte('created_at', dateRange.end.toISOString())
        .in('status', ['completed', 'ready', 'in_progress', 'sent']);

      if (effectiveLocationIds.length === 1) {
        orderIdsQ = orderIdsQ.eq('location_id', effectiveLocationIds[0]);
      } else {
        orderIdsQ = orderIdsQ.in('location_id', effectiveLocationIds);
      }

      const { data: orderIds } = await orderIdsQ;
      const ids = (orderIds || []).map(o => o.id);

      if (ids.length === 0) return [];

      const { data: items, error } = await supabase
        .from('order_items')
        .select('product_id, name, quantity, total, products(id, sku, image_url)')
        .in('order_id', ids);

      if (error) throw error;

      // Aggregate by product
      const productMap: Record<string, TopProductItem> = {};
      (items || []).forEach((item: any) => {
        const key = item.product_id || item.name;
        if (!productMap[key]) {
          productMap[key] = {
            productId: item.product_id,
            name: item.name,
            sku: item.products?.sku || null,
            quantity: 0,
            revenue: 0,
            imageUrl: item.products?.image_url || null,
          };
        }
        productMap[key].quantity += item.quantity || 1;
        productMap[key].revenue += Number(item.total) || 0;
      });

      return Object.values(productMap);
    },
    enabled: effectiveLocationIds.length > 0 && needsTopProducts,
    staleTime: 60000,
  });

  // ---------- Top sales people query ----------
  const topSalesPeopleQuery = useQuery({
    queryKey: ['retail-metrics-top-salespeople', effectiveLocationIds.sort().join(','), timePeriod],
    queryFn: async () => {
      if (effectiveLocationIds.length === 0) return null;

      let ordersQ = supabase
        .from('orders')
        .select('id, total, user_id')
        .gte('created_at', dateRange.start.toISOString())
        .lte('created_at', dateRange.end.toISOString())
        .in('status', ['completed', 'ready', 'in_progress', 'sent'])
        .not('user_id', 'is', null);

      if (effectiveLocationIds.length === 1) {
        ordersQ = ordersQ.eq('location_id', effectiveLocationIds[0]);
      } else {
        ordersQ = ordersQ.in('location_id', effectiveLocationIds);
      }

      const { data: orders, error: e1 } = await ordersQ;
      if (e1) throw e1;

      if (!orders || orders.length === 0) return [];

      // Aggregate by user
      const userMap: Record<string, { revenue: number; orderCount: number }> = {};
      orders.forEach(o => {
        const uid = o.user_id!;
        if (!userMap[uid]) {
          userMap[uid] = { revenue: 0, orderCount: 0 };
        }
        userMap[uid].revenue += Number(o.total) || 0;
        userMap[uid].orderCount += 1;
      });

      // Fetch profiles for user names
      const userIds = Object.keys(userMap);
      const { data: profiles, error: e2 } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, avatar_url')
        .in('id', userIds);
      if (e2) throw e2;

      const profileMap = new Map((profiles || []).map(p => [p.id, p]));

      const result: TopSalespersonItem[] = userIds.map(uid => {
        const profile = profileMap.get(uid);
        const name = profile
          ? [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Unknown'
          : 'Unknown';
        return {
          userId: uid,
          name,
          avatarUrl: profile?.avatar_url || null,
          revenue: userMap[uid].revenue,
          orderCount: userMap[uid].orderCount,
        };
      });

      return result;
    },
    enabled: effectiveLocationIds.length > 0 && needsTopSalesPeople,
    staleTime: 60000,
  });

  // ---- Compute derived metrics from basic orders data ----
  const basicData = basicQuery.data;

  const computeBasicMetrics = () => {
    if (!basicData) return {};

    const { currentOrders, compOrders } = basicData;

    // Revenue
    const currentRevenue = currentOrders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);
    const compRevenue = compOrders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);
    const revenue: SimpleMetric = {
      value: currentRevenue,
      previousValue: compRevenue,
      change: pctChange(currentRevenue, compRevenue),
      comparisonLabel: dateRange.comparisonLabel,
    };

    // Sale count
    const currentCount = currentOrders.length;
    const compCount = compOrders.length;
    const saleCount: SimpleMetric = {
      value: currentCount,
      previousValue: compCount,
      change: pctChange(currentCount, compCount),
      comparisonLabel: dateRange.comparisonLabel,
    };

    // Avg sale value
    const currentAvg = currentCount > 0 ? currentRevenue / currentCount : 0;
    const compAvg = compCount > 0 ? compRevenue / compCount : 0;
    const avgSaleValue: SimpleMetric = {
      value: currentAvg,
      previousValue: compAvg,
      change: pctChange(currentAvg, compAvg),
      comparisonLabel: dateRange.comparisonLabel,
    };

    // Avg items per sale
    const currentItems = currentOrders.reduce(
      (sum, o) => sum + ((o as any).order_items || []).reduce(
        (s: number, i: any) => s + (i.quantity || 1), 0
      ), 0
    );
    const compItems = compOrders.reduce(
      (sum, o) => sum + ((o as any).order_items || []).reduce(
        (s: number, i: any) => s + (i.quantity || 1), 0
      ), 0
    );
    const currentAvgItems = currentCount > 0 ? currentItems / currentCount : 0;
    const compAvgItems = compCount > 0 ? compItems / compCount : 0;
    const avgItemsPerSale: SimpleMetric = {
      value: currentAvgItems,
      previousValue: compAvgItems,
      change: pctChange(currentAvgItems, compAvgItems),
      comparisonLabel: dateRange.comparisonLabel,
    };

    // Sales with customer details
    const currentWithCustomer = currentOrders.filter(o => o.customer_id != null).length;
    const compWithCustomer = compOrders.filter(o => o.customer_id != null).length;
    const currentPct = currentCount > 0 ? (currentWithCustomer / currentCount) * 100 : 0;
    const compPct = compCount > 0 ? (compWithCustomer / compCount) * 100 : 0;
    const salesWithCustomerDetails: PercentageMetric = {
      percentage: currentPct,
      previousPercentage: compPct,
      count: currentWithCustomer,
      total: currentCount,
      comparisonLabel: dateRange.comparisonLabel,
    };

    // Discounted
    const currentDiscountTotal = currentOrders.reduce(
      (sum, o) => sum + (Number(o.discount_amount) || 0), 0
    );
    const compDiscountTotal = compOrders.reduce(
      (sum, o) => sum + (Number(o.discount_amount) || 0), 0
    );
    const currentDiscountedOrders = currentOrders.filter(
      o => (Number(o.discount_amount) || 0) > 0
    ).length;
    const discounted = {
      value: currentDiscountTotal,
      previousValue: compDiscountTotal,
      change: pctChange(currentDiscountTotal, compDiscountTotal),
      comparisonLabel: dateRange.comparisonLabel,
      discountedOrderCount: currentDiscountedOrders,
      totalOrderCount: currentCount,
    };

    return {
      revenue,
      saleCount,
      avgSaleValue,
      avgItemsPerSale,
      salesWithCustomerDetails,
      discounted,
    };
  };

  // ---- Compute gross profit ----
  const computeGrossProfit = () => {
    const gpData = grossProfitQuery.data;
    if (!gpData) return null;

    const { currentItems, compItems, currentOrderDates } = gpData;

    const calcProfit = (items: any[]) => {
      return items.reduce((sum, item) => {
        const revenue = Number(item.total) || 0;
        const cost = (Number((item.products as any)?.cost) || 0) * (item.quantity || 1);
        return sum + (revenue - cost);
      }, 0);
    };

    const currentProfit = calcProfit(currentItems);
    const compProfit = calcProfit(compItems);

    // Build chart data by day/hour
    const orderDateMap = new Map(currentOrderDates.map(o => [o.id, o.created_at]));
    const buckets: Record<string, number> = {};

    currentItems.forEach((item: any) => {
      const orderDate = orderDateMap.get(item.order_id);
      if (!orderDate) return;
      const d = new Date(orderDate);
      const label = timePeriod === 'today'
        ? `${d.getHours()}:00`
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      const revenue = Number(item.total) || 0;
      const cost = (Number((item.products as any)?.cost) || 0) * (item.quantity || 1);
      buckets[label] = (buckets[label] || 0) + (revenue - cost);
    });

    const chartData: GrossProfitChartPoint[] = Object.entries(buckets).map(([label, grossProfit]) => ({
      label,
      grossProfit,
    }));

    return {
      value: currentProfit,
      previousValue: compProfit,
      change: pctChange(currentProfit, compProfit),
      comparisonLabel: dateRange.comparisonLabel,
      chartData,
    };
  };

  // ---- Compute my sales performance ----
  const computeMySales = () => {
    const myData = mySalesQuery.data;
    if (!myData) return null;

    const { currentOrders, compOrders } = myData;
    const currentRevenue = currentOrders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);
    const compRevenue = compOrders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);

    return {
      value: currentRevenue,
      previousValue: compRevenue,
      change: pctChange(currentRevenue, compRevenue),
      comparisonLabel: dateRange.comparisonLabel,
      orderCount: currentOrders.length,
      previousOrderCount: compOrders.length,
    };
  };

  const basicMetrics = computeBasicMetrics();

  return {
    revenue: needsBasicOrders ? (basicMetrics.revenue || null) : null,
    grossProfit: needsGrossProfit ? computeGrossProfit() : null,
    saleCount: needsBasicOrders ? (basicMetrics.saleCount || null) : null,
    salesWithCustomerDetails: needsBasicOrders ? (basicMetrics.salesWithCustomerDetails || null) : null,
    avgItemsPerSale: needsBasicOrders ? (basicMetrics.avgItemsPerSale || null) : null,
    avgSaleValue: needsBasicOrders ? (basicMetrics.avgSaleValue || null) : null,
    discounted: needsBasicOrders ? (basicMetrics.discounted || null) : null,
    mySalesPerformance: needsMySales ? computeMySales() : null,
    topProductsSold: needsTopProducts ? (topProductsQuery.data as TopProductItem[] | null) : null,
    topSalesPeople: needsTopSalesPeople ? (topSalesPeopleQuery.data as TopSalespersonItem[] | null) : null,
    comparisonLabel: dateRange.comparisonLabel,
    isLoading: basicQuery.isLoading || grossProfitQuery.isLoading ||
               mySalesQuery.isLoading || topProductsQuery.isLoading ||
               topSalesPeopleQuery.isLoading,
  };
}
